import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AnnouncementAttachment, AnnouncementRead } from '@isophi/mng-api';
import { BehaviorSubject, combineLatest, filter, map, ReplaySubject, shareReplay, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../base-component/base.component';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin';
import { ToastService } from '../../services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { downloadFile, downloadImageOrVideo } from '../../utils/file.utils';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
    selector: 'isophi-announcement',
    templateUrl: './announcement.component.html',
    styleUrls: ['./announcement.component.scss'],
    standalone: false
})
export class AnnouncementComponent extends BaseComponent implements OnDestroy, OnInit {
  private readonly _toastService = inject(ToastService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _spinnerService = inject(NgxSpinnerService);
  private readonly _elRef = inject(ElementRef<HTMLElement>);

  private lightbox: PhotoSwipeLightbox | null = null;

  @Input() set announcement(value: AnnouncementRead) {
    this.currentAnnouncement$.next(value);
  }

  /** Permanently expand content text */
  @Input() expandedText = false;

  /** Permanently expand content text */
  @Input() showHeader = true;

  @Input() profilePicture = false;

  /** When non-null, the star button is shown with this state */
  @Input() isStarred: boolean | null = null;

  /** When non-null, the mark-as-read button is shown with this state */
  @Input() isRead: boolean | null = null;

  @Output() toggleStarred = new EventEmitter<void>();
  @Output() toggleRead = new EventEmitter<void>();

  @ViewChild('text') set setTextElementRef(el: ElementRef<HTMLDivElement>) {
    // push eval to next micro-task so angular will detect changes
    setTimeout(() => {
      // detect if text is overflowing and expander should be shown
      if (el && el.nativeElement.offsetHeight < el.nativeElement.scrollHeight && !this.expandedText) {
        this.showTextExpander$.next(true);
      }
      if (this.expandedText) {
        this.textExpanded$.next(true);
      }
    }, 0);
  }

  protected readonly currentAnnouncement$ = new ReplaySubject<AnnouncementRead>();
  protected readonly textExpanded$ = new BehaviorSubject(false);
  protected readonly showTextExpander$ = new BehaviorSubject(false);

  protected readonly announcementMedia$ = this.currentAnnouncement$.pipe(
    filter((announcement) => !!announcement),
    map((announcement) => {
      const media: AnnouncementAttachment[] = [];
      if (announcement.videos?.length > 0) media.push(...announcement.videos);
      if (announcement.images?.length > 0) media.push(...announcement.images);
      return media;
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected toggleExpander(target: 'body' | 'toggle') {
    combineLatest([this.showTextExpander$, this.textExpanded$])
      .pipe(take(1))
      .subscribe(([allowed, expanded]) => {
        if (!allowed || (target === 'body' && expanded)) {
          return;
        }
        this.textExpanded$.next(!expanded);
      });
  }

  ngOnInit(): void {
    combineLatest([this.announcementMedia$, this.currentAnnouncement$])
      .pipe(
        filter(([media]) => !!media && media.length > 0),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([_, announcement]) => {
        if (this.lightbox) {
          this.lightbox.destroy();
        }
        setTimeout(() => {
          this.lightbox = new PhotoSwipeLightbox({
            gallery: '#img-gallery-' + announcement.id,
            children: 'a',
            showHideAnimationType: 'fade',
            initialZoomLevel: 'fit',
            appendToEl: this._elRef.nativeElement,

            pswpModule: () => import('photoswipe'),
          });
          const videoPlugin = new PhotoSwipeVideoPlugin(this.lightbox, {
            videoAttributes: { controls: '', playsinline: '', preload: 'none', autoplay: true },
            autoplay: true,
            preventDragOffset: 40,
          });
          this.lightbox.init();

          // Add custom download button
          this.lightbox.on('uiRegister', () => {
            this.lightbox?.pswp?.ui?.registerElement({
              name: 'download-button',
              order: 9,
              isButton: true,
              tagName: 'a',

              html: {
                size: 26,
                isCustomSVG: true,
                inner: `<svg viewBox="-8.4 -8.4 40.80 40.80" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12.5535 16.5061C12.4114 16.6615 12.2106 16.75 12 16.75C11.7894 16.75 11.5886 16.6615 11.4465 16.5061L7.44648 12.1311C7.16698 11.8254 7.18822 11.351 7.49392 11.0715C7.79963 10.792 8.27402 10.8132 8.55352 11.1189L11.25 14.0682V3C11.25 2.58579 11.5858 2.25 12 2.25C12.4142 2.25 12.75 2.58579 12.75 3V14.0682L15.4465 11.1189C15.726 10.8132 16.2004 10.792 16.5061 11.0715C16.8118 11.351 16.833 11.8254 16.5535 12.1311L12.5535 16.5061Z" fill="#ffffff"></path> <path d="M3.75 15C3.75 14.5858 3.41422 14.25 3 14.25C2.58579 14.25 2.25 14.5858 2.25 15V15.0549C2.24998 16.4225 2.24996 17.5248 2.36652 18.3918C2.48754 19.2919 2.74643 20.0497 3.34835 20.6516C3.95027 21.2536 4.70814 21.5125 5.60825 21.6335C6.47522 21.75 7.57754 21.75 8.94513 21.75H15.0549C16.4225 21.75 17.5248 21.75 18.3918 21.6335C19.2919 21.5125 20.0497 21.2536 20.6517 20.6516C21.2536 20.0497 21.5125 19.2919 21.6335 18.3918C21.75 17.5248 21.75 16.4225 21.75 15.0549V15C21.75 14.5858 21.4142 14.25 21 14.25C20.5858 14.25 20.25 14.5858 20.25 15C20.25 16.4354 20.2484 17.4365 20.1469 18.1919C20.0482 18.9257 19.8678 19.3142 19.591 19.591C19.3142 19.8678 18.9257 20.0482 18.1919 20.1469C17.4365 20.2484 16.4354 20.25 15 20.25H9C7.56459 20.25 6.56347 20.2484 5.80812 20.1469C5.07435 20.0482 4.68577 19.8678 4.40901 19.591C4.13225 19.3142 3.9518 18.9257 3.85315 18.1919C3.75159 17.4365 3.75 16.4354 3.75 15Z" fill="#ffffff"></path> </g></svg>`,
                outlineID: 'download-icon',
              },

              onInit: (el: HTMLElement, pswp) => {
                // on download click, fetch image
                el.addEventListener('click', async (e) => {
                  e.preventDefault();
                  const currSlide = pswp.currSlide;
                  if (!currSlide?.data?.src) return;

                  try {
                    this._toastService.success(this._translocoService.translate('shared/savingStarted'));
                    const fileType = currSlide.data.type as 'image' | 'video';
                    await downloadImageOrVideo(currSlide.data.src, fileType);
                    switch (currSlide.data.type) {
                      case 'video':
                        this._toastService.success(this._translocoService.translate('shared/saveVideoSuccess'));
                        break;
                      case 'image':
                        this._toastService.success(this._translocoService.translate('shared/savePhotoSuccess'));
                        break;
                    }
                  } catch (error) {
                    if (error?.toString()?.includes('denied permission')) {
                      this._toastService.error(this._translocoService.translate('shared/failurePermission'));
                      return;
                    }

                    switch (currSlide.data.type) {
                      case 'video':
                        this._toastService.error(this._translocoService.translate('shared/saveVideoFailure'));
                        break;
                      case 'image':
                        this._toastService.error(this._translocoService.translate('shared/savePhotoFailure'));
                        break;
                    }
                  }
                });
              },
            });
          });

          // update slides image dimensions when image loads
          this.lightbox.on('contentLoadImage', (event) => {
            const content = event.content;
            const el = content.element;

            if (el instanceof HTMLImageElement) {
              const onImageLoad = () => {
                content.data.width = el.naturalWidth;
                content.data.height = el.naturalHeight;

                const parentEl = content.slide?.data?.element;

                // Save its height so we can use it later
                if (parentEl) {
                  parentEl?.setAttribute('data-pswp-height', content.data.height.toString());
                  parentEl?.setAttribute('data-pswp-width', content.data.width.toString());
                }

                if (content.slide) {
                  content.slide.width = content.data.width;
                  content.slide.height = content.data.height;

                  content.slide.resize();
                }
              };
              content.onLoaded = () => {
                onImageLoad();
              };
            }
          });

          // images could have wrong dimensions when swiping between them on first lightbox init
          this.lightbox.on('slideInit', (event) => {
            const parentEl = event.slide.content.data.element;

            if (parentEl) {
              const savedHeight = Number(parentEl.getAttribute('data-pswp-height'));
              const savedWidth = Number(parentEl.getAttribute('data-pswp-width'));

              const slide = event.slide;

              slide.width = savedWidth;
              slide.height = savedHeight;
            }
          });

          // Provide multiple sources to video
          this.lightbox.addFilter('itemData', (itemData) => {
            const element = itemData.element;
            if (element) {
              const rawSources = element.getAttribute('data-sources');
              if (rawSources) {
                itemData['videoSources'] = JSON.parse(rawSources);
              }
            }
            return itemData;
          });
        }, 200);
      });
  }

  downloadAttachment(link: string) {
    this._spinnerService.show();
    downloadFile(link)
      .then(() => {
        this._spinnerService.hide();
      })
      .catch(() => {
        this._spinnerService.hide();
        this._toastService.error(this._translocoService.translate('shared/file/downloadError'));
      });
  }

  getJsonSources(media: AnnouncementAttachment): string {
    if (media.fileType !== 'video') {
      return '';
    }
    const sources =
      media.convertedMp4 || media.convertedWebm
        ? [
            { src: media.convertedWebm, type: 'video/webm' },
            { src: media.convertedMp4, type: 'video/mp4' },
            { src: media.convertedMp4, type: '' },
          ]
        : [{ src: media.file, type: 'video/mp4' }];

    return JSON.stringify(sources);
  }

  override ngOnDestroy(): void {
    this._onDestroy();
    this.lightbox?.destroy();
  }
}
